import React from "react";
import { Row, Col } from "react-bootstrap";
import {
  type FieldError,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import TooltipTrigger from "@/components/shared/TooltipTrigger.tsx";
import { FiHelpCircle } from "react-icons/fi";
import DateInput from "@/components/shared/forms/DateInput";
import { LanguageMultiSelect } from "@/components/shared/LanguageMultiSelect";

type LabelAs = "label" | "legend" | "static";

type FormFieldRowProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  name: TName;
  labelText: string;
  errors: FieldErrors<TFieldValues>;
  tooltip?: string;
  tooltipMap?: Map<string, string>;
  labelCol?: number;
  inputCol?: number;
  isRequired?: boolean;
  labelAs?: LabelAs;
  idPrefix?: string;
  children: (
    fieldId: string,
    fieldName: TName,
    validationProps: ValidationProps
  ) => React.JSX.Element;
};

export default function FormFieldRow<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(props: FormFieldRowProps<TFieldValues, TName>) {
  const {
    name,
    labelText,
    errors,
    tooltip,
    tooltipMap,
    labelCol,
    inputCol,
    isRequired,
    labelAs,
    idPrefix,
    children,
  } = props;

  const fieldError = errors[name] as FieldError | undefined;

  return (
    <FormFieldRowInternal
      name={name}
      labelText={labelText}
      fieldError={fieldError}
      tooltip={tooltip}
      tooltipMap={tooltipMap}
      labelCol={labelCol}
      inputCol={inputCol}
      isRequired={isRequired}
      labelAs={labelAs}
      idPrefix={idPrefix}
    >
      {children}
    </FormFieldRowInternal>
  );
}

// Extra props we want to be able to read/add to the child control
type ValidationProps = {
  className?: string;
  "aria-invalid"?: boolean;
  "aria-required"?: boolean;
  "aria-describedby"?: string;
};

type FormFieldRowInternalProps<TName extends string = string> = {
  name: TName;
  labelText: string;
  fieldError?: FieldError;
  tooltip?: string;
  tooltipMap?: Map<string, string>;
  labelCol?: number;
  inputCol?: number;
  isRequired?: boolean;
  labelAs?: LabelAs;
  idPrefix?: string;
  children: (
    fieldId: string,
    fieldName: TName,
    validationProps: ValidationProps
  ) => React.JSX.Element;
};

function FormFieldRowInternal<TName extends string = string>({
  name,
  labelText,
  fieldError,
  tooltip,
  tooltipMap,
  labelCol = 4,
  inputCol = 6,
  isRequired,
  labelAs = "label",
  idPrefix,
  children,
}: FormFieldRowInternalProps<TName>) {
  const hasError = !!fieldError;

  const fieldId = idPrefix ? `${idPrefix}${name}` : name;
  const tooltipText = tooltip ?? tooltipMap?.get(name);
  const tooltipId = tooltipText ? `${fieldId}-description` : undefined;
  const errorId = hasError ? `${fieldId}-error` : undefined;

  const isLegendGroup = labelAs === "legend";

  const validationProps: ValidationProps = {
    "aria-invalid": !isLegendGroup && hasError ? true : undefined,
    "aria-required": isRequired || undefined,
    "aria-describedby": isLegendGroup
      ? undefined
      : [tooltipId, errorId].filter(Boolean).join(" ") || undefined,
  };

  let childElement = children(fieldId, name, validationProps);

  // Native <input type="date"> renders its own "mm/dd/yyyy" segment
  // placeholder, but that's unreliably announced by screen readers - it's
  // not a real accessible description. An aria-describedby pointing at a
  // hint span isn't reliable here either: confirmed with NVDA that it's
  // silently dropped, almost certainly because native date inputs have
  // internal shadow-DOM parts (separate month/day/year spinbuttons) that
  // browsers don't consistently forward aria-describedby through. The
  // accessible *name* (built from the <label>) doesn't have that problem -
  // it's read reliably on focus - so the format hint is folded into the
  // label text instead, further down.
  // Checked via the element's component reference (childElement.type), not
  // childElement.props.type: DateInput defaults type to "date" internally,
  // so call sites that don't pass an explicit type prop - most of them -
  // would otherwise look untyped from here. All native date fields go
  // through the DateInput wrapper (no bare <input type="date"> call sites
  // remain), so that's the only case to match.
  const isDateField =
    React.isValidElement<{ type?: string }>(childElement) &&
    childElement.type === DateInput &&
    (childElement.props.type ?? "date") === "date";

  if (
    React.isValidElement<ValidationProps>(childElement) &&
    childElement.type !== "div" &&
    childElement.type !== LanguageMultiSelect
  ) {
    const existingClassName = childElement.props.className ?? "";
    const existingDescribedBy = childElement.props["aria-describedby"];

    childElement = React.cloneElement<ValidationProps>(childElement, {
      className: `${existingClassName} ${hasError ? "is-invalid" : ""}`.trim(),
      "aria-invalid": isLegendGroup
        ? childElement.props["aria-invalid"]
        : hasError || childElement.props["aria-invalid"] || undefined,
      "aria-required":
        isRequired || childElement.props["aria-required"] || undefined,
      "aria-describedby": isLegendGroup
        ? existingDescribedBy
        : [existingDescribedBy, tooltipId, errorId].filter(Boolean).join(" ") ||
          undefined,
    });
  }

  // Each of the native date input's month/day/year segments gets its own
  // accessible name, built by the browser as "<Segment> " + this label's
  // full text - so whatever hidden text lives here gets announced three
  // times over as a user moves between segments. Kept as one short combined
  // phrase (rather than a separate required span and format-hint span) to
  // minimize how much repeats per segment.
  const dateFormatHint = isDateField ? (
    <span className="visually-hidden">
      {isRequired ? " required, format mm/dd/yyyy" : " format mm/dd/yyyy"}
    </span>
  ) : null;

  const requiredMark = isRequired ? (
    <span
      className="required-indicator"
      aria-hidden="true"
      style={{ color: "#cc0000", marginLeft: 4 }}
    >
      *
    </span>
  ) : null;

  const tooltipNode = tooltipText ? (
    <>
      <TooltipTrigger tooltipText={tooltipText}>
        <FiHelpCircle
          size={16}
          className="text-muted"
          style={{ marginLeft: 6, cursor: "pointer" }}
          aria-hidden="true"
        />
      </TooltipTrigger>
      {/* Screen readers get this via aria-describedby on the field itself;
          the visual tooltip above only shows on hover/focus of the icon,
          which isn't reliably reachable by keyboard/AT on its own. */}
      <span id={tooltipId} className="visually-hidden">
        {tooltipText}
      </span>
    </>
  ) : null;

  const inputColumn = (
    <Col lg={inputCol}>
      {childElement}
      {fieldError && (
        <p id={errorId} role="alert" className="form-error">
          {fieldError.message}
        </p>
      )}
    </Col>
  );

  if (labelAs === "legend") {
    const legendId = `${fieldId}-legend`;
    return (
      <fieldset
        className="form-group row"
        role="radiogroup"
        aria-required={isRequired || undefined}
        aria-invalid={hasError || undefined}
        aria-labelledby={legendId}
        aria-describedby={
          [tooltipId, errorId].filter(Boolean).join(" ") || undefined
        }
      >
        <Col
          as="legend"
          id={legendId}
          lg={labelCol}
          className="text-lg-end col-form-label pt-0"
        >
          {labelText}
          {requiredMark}
          {tooltipNode}
        </Col>
        {inputColumn}
      </fieldset>
    );
  }

  if (labelAs === "static") {
    return (
      <Row className="form-group row">
        <Col lg={labelCol} className="text-lg-end">
          <span className="col-form-label">
            {labelText}
            {requiredMark}
            {dateFormatHint}
          </span>
          {tooltipNode}
        </Col>
        {inputColumn}
      </Row>
    );
  }

  return (
    <Row className="form-group row">
      <Col lg={labelCol} className="text-lg-end">
        <label htmlFor={fieldId} className="col-form-label">
          {labelText}
          {requiredMark}
          {dateFormatHint}
        </label>
        {tooltipNode}
      </Col>
      {inputColumn}
    </Row>
  );
}
